import { html, nothing } from "lit";
import "./rich-runtime-block.ts";
import { renderCopyAsMarkdownButton } from "./copy-as-markdown.ts";
import { extractTextCached, extractThinkingCached, formatReasoningMarkdown } from "./message-extract.ts";
import { normalizeRoleForGrouping } from "./message-normalizer.ts";
import {
  buildMessageSidebarBlocks,
  buildStructuredSidebarContent,
  detectJsonPreview,
  extractImages,
  jsonSummaryLabel,
  type MessageImageBlock,
} from "./transcript.ts";
import { renderToolCardSidebar, extractToolCards } from "./tool-cards.ts";
import { renderRichMarkdown } from "./rich-markdown-render.ts";
import { renderRichIframeBlock, renderRichSvgBlock } from "./rich-static-block.ts";
import { openExternalUrlSafe } from "../open-external-url.ts";
import type { ChatSidebarBlock, ChatSidebarContent, ToolCard } from "../types/chat-types.ts";

export type RichMessageRenderOptions = {
  showReasoning: boolean;
  showToolCalls: boolean;
  isStreaming?: boolean;
  onOpenSidebar?: (content: ChatSidebarContent) => void;
};

function renderRichImages(images: MessageImageBlock[]) {
  if (images.length === 0) {
    return nothing;
  }
  return html`
    <div class="chat-rich-message__images">
      ${images.map(
        (img) => html`
          <img
            src=${img.url}
            alt=${img.alt ?? "Attached image"}
            class="chat-message-image chat-rich-message__image"
            @click=${() => openExternalUrlSafe(img.url, { allowDataImage: true })}
          />
        `,
      )}
    </div>
  `;
}

function renderToolCards(toolCards: ToolCard[], onOpenSidebar?: (content: ChatSidebarContent) => void) {
  if (toolCards.length === 0) {
    return nothing;
  }
  return html`
    <div class="chat-rich-message__tool-cards">
      ${toolCards.map((card) => renderToolCardSidebar(card, onOpenSidebar))}
    </div>
  `;
}

function renderRichBlock(block: ChatSidebarBlock) {
  switch (block.type) {
    case "markdown":
      return renderRichMarkdown(block.content);
    case "json": {
      const pretty = JSON.stringify(block.data, null, 2);
      return html`
        <details class="chat-rich-json-collapse">
          <summary class="chat-rich-json-summary">
            <span class="chat-rich-json-badge">JSON</span>
            <span class="chat-rich-json-label">${block.label ?? jsonSummaryLabel(block.data)}</span>
          </summary>
          <pre class="chat-rich-json-content"><code>${pretty}</code></pre>
        </details>
      `;
    }
    case "diagram-mermaid":
    case "chart-vega-lite":
    case "math-latex":
      return html`<openclaw-rich-runtime-block .block=${block} context="inline"></openclaw-rich-runtime-block>`;
    case "embedded-markdown":
      return html`
        <div class="chat-rich-placeholder chat-rich-placeholder--interactive">
          <div class="chat-rich-placeholder__title">${block.label ?? "Embedded Markdown"}</div>
          ${renderRichMarkdown(block.content)}
        </div>
      `;
    case "svg":
      return renderRichSvgBlock(block, "inline");
    case "iframe":
      return renderRichIframeBlock(block, "inline");
    case "code":
    case "image":
      return nothing;
  }
}

export function renderRichMessage(message: unknown, opts: RichMessageRenderOptions) {
  const renderStreamingCaret = (enabled: boolean) =>
    enabled
      ? html`<span class="chat-rich-streaming-caret" aria-hidden="true"></span>`
      : nothing;
  const record = message as Record<string, unknown>;
  const role = normalizeRoleForGrouping(typeof record.role === "string" ? record.role : "unknown");
  const text = extractTextCached(message)?.trim() ?? "";
  const thinking = opts.showReasoning ? extractThinkingCached(message)?.trim() ?? "" : "";
  const toolCards = opts.showToolCalls ? extractToolCards(message) : [];
  const images = extractImages(message);
  const isUser = role === "user";
  const canCopy = role === "assistant" && text.length > 0;
  const jsonResult = text && !opts.isStreaming ? detectJsonPreview(text) : null;
  const sidebarBlocks = buildMessageSidebarBlocks({ message, text, images });
  const inlineBlocks = sidebarBlocks.filter((block) => block.type !== "image" && block.type !== "code");
  const canExpand = role === "assistant" && sidebarBlocks.length > 0 && Boolean(opts.onOpenSidebar);
  const bubbleClass = [
    "chat-rich-message__bubble",
    isUser ? "chat-rich-message__bubble--user" : "chat-rich-message__bubble--assistant",
    opts.isStreaming ? "chat-rich-message__bubble--streaming" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (inlineBlocks.length === 0 && !thinking && toolCards.length === 0 && images.length === 0) {
    return nothing;
  }

  return html`
    <article class="chat-rich-message ${isUser ? "chat-rich-message--user" : "chat-rich-message--assistant"}">
      <div class=${bubbleClass}>
        ${canExpand
          ? html`
              <div class="chat-bubble-actions">
                <button
                  class="chat-expand-btn"
                  type="button"
                  title="Open in canvas"
                  aria-label="Open in canvas"
                  @click=${() =>
                    opts.onOpenSidebar?.(
                      buildStructuredSidebarContent({
                        title: "Rich message",
                        message,
                        text,
                        images,
                        jsonResult,
                      }),
                    )}
                >
                  <span class="chat-expand-btn__icon" aria-hidden="true">↗</span>
                </button>
              </div>
            `
          : nothing}
        ${renderRichImages(images)}
        ${thinking ? html`<div class="chat-rich-message__thinking">${renderRichMarkdown(formatReasoningMarkdown(thinking))}</div>` : nothing}
        ${inlineBlocks.length > 0
          ? html`
              <div class="chat-rich-message__inline-rich ${opts.isStreaming ? "chat-rich-message__inline-rich--streaming" : ""}">
                ${inlineBlocks.map((block) =>
                  block.type === "markdown"
                    ? renderRichMarkdown(block.content, { isStreaming: Boolean(opts.isStreaming) })
                    : renderRichBlock(block),
                )}
                ${opts.isStreaming && !inlineBlocks.some((block) => block.type === "markdown")
                  ? renderStreamingCaret(true)
                  : nothing}
              </div>
            `
          : renderStreamingCaret(Boolean(opts.isStreaming))}
        ${renderToolCards(toolCards, opts.onOpenSidebar)}
      </div>
      ${canCopy ? html`<div class="chat-message-actions">${renderCopyAsMarkdownButton(text)}</div>` : nothing}
    </article>
  `;
}
